import { createSelector } from "@reduxjs/toolkit";
import config from "../../../../../../config/common";
import { selectSearchState } from "../../selectors/shared";
import { buildProduct } from "../productTile/selectors";
import { getPageSize } from "../../query/selectors";

const emptyObject = {};
const emptyArray = [];

export const selectSearchResultsDomain = (state) => {
  const search = selectSearchState(state);

  return {
    products: search.products ?? emptyArray,
    itemCount: search.itemCount ?? 0,
    offset: search.offset ?? emptyObject,
    isLoading: search.isLoading ?? false,
    isLoadingMore: search.isLoadingMore ?? false,
    isLoadingPrevious: search.isLoadingPrevious ?? false,
    showLoadMore: search.showLoadMore ?? false,
    showLoadPrevious: search.showLoadPrevious ?? false,
    tileToFocus: search.tileToFocus ?? emptyObject,
  };
};

export const selectSearchShowLoadMore = (state) =>
  selectSearchResultsDomain(state).showLoadMore;

export const selectSearchShowLoadPrevious = (state) =>
  selectSearchResultsDomain(state).showLoadPrevious;

export const selectSearchTileToFocus = (state) =>
  selectSearchResultsDomain(state).tileToFocus;

export const getOffset = (state) => state.search.offset;

export const getProducts = ({ search: { products } }) => products;

export const getItemCount = ({ search: { itemCount } }) => itemCount;

export const getHasShowableVideo = ({ search: { products } }) =>
  products.some(({ showVideo }) => showVideo);

export const getShowLoadMore = ({ search: { showLoadMore } }) => showLoadMore;

export const getShowLoadPrevious = ({ search: { showLoadPrevious } }) =>
  showLoadPrevious;

export const getTileToFocus = ({ search: { tileToFocus } }) => tileToFocus;

export const getPages = createSelector(
  [getProducts, getOffset, getPageSize, (state) => state],
  (products, offsetState, pageSize, state) => {
    const offset = offsetState.lower / pageSize;
    const getPageIndex = (index) => Math.floor(index / pageSize);
    const getIndexOnThePage = (index) => index % pageSize;

    return products.reduce((pages, product, productIndex) => {
      const pageIndex = getPageIndex(productIndex);
      const pageNumber = offset + pageIndex + 1;
      const onPageProductIndex = getIndexOnThePage(productIndex);
      const pageItem = {
        product: buildProduct(
          {
            product,
            productIndex,
          },
          state,
        ),
        onPageProductIndex,
      };

      pages[pageIndex] = pages[pageIndex]
        ? {
            ...pages[pageIndex],
            products: [...pages[pageIndex].products, pageItem],
          }
        : {
            products: [pageItem],
            pageNumber,
          };

      return pages;
    }, []);
  },
);

const getPaginationPage = ({
  showLoadMore,
  showLoadPrevious,
  initialPageNumber,
  itemCount,
  offset,
  pageSize,
}) => ({
  previous: showLoadPrevious ? Math.ceil(offset.lower / pageSize) : null,
  initial: initialPageNumber,
  next: showLoadMore ? Math.ceil(offset.upper / pageSize) + 1 : null,
  max: Math.ceil(itemCount / pageSize),
});

const getPaginationOffset = ({
  showLoadMore,
  showLoadPrevious,
  offset,
  pageSize,
}) => ({
  previous: showLoadPrevious ? offset.lower - pageSize : null,
  initial: offset.initial,
  next: showLoadMore ? offset.upper : null,
});

export const getPaginationData = (
  state,
  pageSize = config.api.product.search.pageSize,
) => {
  const { itemCount, showLoadMore, showLoadPrevious, offset } = state.search;

  const hasResults = itemCount > 0;

  if (!hasResults) {
    return {
      meta: {
        hasResults,
        canLoadMore: showLoadMore,
        canLoadPrevious: showLoadPrevious,
      },
      page: {
        previous: null,
        initial: null,
        next: null,
        max: null,
      },
      offset: {
        previous: null,
        initial: null,
        next: null,
      },
    };
  }

  const initialPageNumber = Math.ceil(offset.initial / pageSize) + 1;

  return {
    meta: {
      hasResults,
      canLoadMore: showLoadMore,
      canLoadPrevious: showLoadPrevious,
    },
    page: getPaginationPage({
      showLoadMore,
      showLoadPrevious,
      initialPageNumber,
      itemCount,
      offset,
      pageSize,
    }),
    offset: getPaginationOffset({
      showLoadMore,
      showLoadPrevious,
      offset,
      pageSize,
    }),
  };
};

export const getPaginationInitial = (state) =>
  getPaginationData(state).page.initial;

export const getPaginationMax = (state) => getPaginationData(state).page.max;
